import { Component, inject } from '@angular/core';
import { TopicStore } from '../../../stores/topic/topic.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { TopicHelperService } from '../../../services/topic/topic-helper.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { COLOR_TOKENS, ColorToken, Topic } from '../../../models/topic';
import { LandingPageAudience } from '../../../models/commune';
import { hasSeparateLandingPages } from '../../../utility/landing-page.utility';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { IconDropdownComponent } from '../../atoms/icon-dropdown/icon-dropdown.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { InputSwitchModule } from 'primeng/inputswitch';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { EditorModule } from 'primeng/editor';
import { TabMenuModule } from 'primeng/tabmenu';
import { MenuItem } from 'primeng/api';
import { PickListModule } from 'primeng/picklist';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkDropdownComponent } from '../../atoms/bookmark-dropdown/bookmark-dropdown.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { TooltipModule } from 'primeng/tooltip';
import { Ripple } from 'primeng/ripple';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-topic-form',
  standalone: true,
  imports: [
    ButtonModule,
    DividerModule,
    FormFieldWrapperComponent,
    IconDropdownComponent,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    ReactiveFormsModule,
    FormsModule,
    InputSwitchModule,
    WorkspaceDropdownComponent,
    FormFooterComponent,
    EditorModule,
    TabMenuModule,
    PickListModule,
    BookmarkDropdownComponent,
    HeaderComponent,
    TooltipModule,
    Ripple,
    PickListHeaderComponent,
  ],
  templateUrl: './topic-form.component.html',
  styleUrl: './topic-form.component.scss',
})
export class TopicFormComponent extends BaseFormComponent<Topic> {
  readonly store = inject(TopicStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly globalStore = inject(GlobalsStore);
  readonly topicHelper = inject(TopicHelperService);
  selectedTab = 'general';

  bookmarkCategories: BookmarkCategory[] = [];
  selectedCategories: BookmarkCategory[] = [];
  /** category id -> comma-joined titles of other topics it's already assigned to */
  categoryConflicts: Record<string, string> = {};

  /** on the shared landing page, or the internal one once separated */
  showOnLandingPage = false;
  /** on the public landing page, only used once the landing pages are separated */
  showOnPublicLandingPage = false;
  private pendingLandingPageVisibility: Partial<
    Record<LandingPageAudience, boolean>
  > | null = null;

  readonly colorTokens = COLOR_TOKENS;

  tabItems: MenuItem[] = [
    {
      label: 'Grundeinstellungen',
      icon: 'fas fa-book-open',
      id: 'general',
      routerLink: ['../general'],
    },
    {
      label: 'Themenbereiche',
      icon: 'fas fa-layer-group',
      id: 'lesezeichen',
      routerLink: ['../lesezeichen'],
    },
  ];
  activeTabItem: MenuItem = this.tabItems[0];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    // A newly created topic only gets its _id once the server responds, so the
    // landing page toggles have to wait for that before they can write into
    // the commune's landing page lists.
    this.onceCreated(
      () => this.pendingLandingPageVisibility !== null,
      id => {
        const topic = this.store.getById(id);
        if (topic && this.pendingLandingPageVisibility) {
          this.applyLandingPages(topic, this.pendingLandingPageVisibility);
        }
        this.pendingLandingPageVisibility = null;
      }
    );
  }

  /**
   * Initializes a form with default or pre-existing values for editing or creation.
   * This includes setting the active tab based on route parameters, configuring
   * default values for form fields, and marking the form as initialized.
   *
   * @return {void} This method does not return any value.
   */
  initializeForm(): void {
    this.selectedTab =
      this.routingService.getParameterFromRoute('tab') || 'general';
    if (this.selectedTab !== 'general') this.activeTabItem = this.tabItems[1];

    this.setCategorySelection();

    const commune = this.globalStore.activeCommune();
    const topicId = this.itemToEdit?._id || '';
    this.showOnLandingPage = this.topicHelper.isOnLandingPage(
      commune,
      topicId,
      'internal'
    );
    this.showOnPublicLandingPage = this.topicHelper.isOnLandingPage(
      commune,
      topicId,
      'public'
    );

    this.form = this.fb.group({
      title: [this.itemToEdit?.title || '', Validators.required],
      description: [this.itemToEdit?.description || ''],
      icon: [this.itemToEdit?.icon || '', Validators.required],
      color: [this.itemToEdit?.color || ''],
      defaultBookmark: [this.itemToEdit?.defaultBookmark || null],
      bookmarkCategories: [this.itemToEdit?.bookmarkCategories || []],
      published: [this.topicHelper.getPublicVisibility(this.itemToEdit)],
      visibleInInternalAtlas: [this.itemToEdit?.visibleInInternalAtlas ?? true],
      spotlight: [this.itemToEdit?.spotlight || false],
      workspaces: [this.itemToEdit?.workspaces || []],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission logic when the form or action is triggered.
   * Executes the defined submit process and only then writes the landing page
   * toggles into the commune's landing pages (deferred for a new topic until
   * it has an id), so cancelling the form leaves the landing pages alone.
   *
   * @return {void} This method does not return a value.
   */
  onSubmit(): void {
    if (this.form.valid) {
      const landingPages = this.getLandingPageSelection();
      if (this.itemToEdit && this.itemToEdit._id) {
        this.applyLandingPages(this.itemToEdit, landingPages);
      } else {
        this.pendingLandingPageVisibility = landingPages;
      }
    }
    this.submit();
  }

  /**
   * The landing pages the topic should be on according to the toggles. With
   * separated landing pages, a landing page also requires the topic to be
   * visible in that atlas.
   *
   * @return {Partial<Record<LandingPageAudience, boolean>>} Per landing page whether the topic belongs on it.
   */
  private getLandingPageSelection(): Partial<
    Record<LandingPageAudience, boolean>
  > {
    if (!this.separateLandingPages) {
      return { internal: this.showOnLandingPage };
    }
    const { published, visibleInInternalAtlas } = this.form.getRawValue();
    return {
      public: this.showOnPublicLandingPage && !!published,
      internal: this.showOnLandingPage && !!visibleInInternalAtlas,
    };
  }

  /**
   * Adds the topic to or removes it from the given landing pages, writing only
   * the ones that actually change.
   *
   * @param {Topic} topic - The saved topic.
   * @param {Partial<Record<LandingPageAudience, boolean>>} landingPages - Per landing page whether the topic belongs on it.
   * @return {void} Does not return a value.
   */
  private applyLandingPages(
    topic: Topic,
    landingPages: Partial<Record<LandingPageAudience, boolean>>
  ): void {
    (Object.keys(landingPages) as LandingPageAudience[]).forEach(audience => {
      const shown = !!landingPages[audience];
      const isShown = this.topicHelper.isOnLandingPage(
        this.globalStore.activeCommune(),
        topic._id,
        audience
      );
      if (shown !== isShown) {
        this.topicHelper.setOnLandingPage(topic, shown, audience);
      }
    });
  }

  /**
   * Handles the logic for setting the category selection for bookmarks.
   * Updates the `selectedCategories` based on the categories included in
   * the `itemToEdit` bookmark and populates the `bookmarkCategories` list
   * with the remaining categories.
   *
   * @return {void} Does not return any value.
   */
  setCategorySelection(): void {
    const bookmarkCategories = this.bookmarkCategoryStore.asArray();
    this.bookmarkCategories = [];
    this.categoryConflicts = {};
    if (this.itemToEdit && this.itemToEdit.bookmarkCategories) {
      this.selectedCategories = bookmarkCategories.filter(category => {
        if (this.itemToEdit?.bookmarkCategories.includes(category._id)) {
          return true;
        } else {
          this.bookmarkCategories.push(category);
          this.setConflictInfo(category);
          return false;
        }
      });
    } else {
      this.selectedCategories = [];
      this.bookmarkCategories = bookmarkCategories;
      this.bookmarkCategories.forEach(category =>
        this.setConflictInfo(category)
      );
    }
  }

  /**
   * Records, for a source-list (not-yet-assigned) bookmark category, which OTHER
   * topic(s) it's already assigned to - purely informational, doesn't block
   * assigning it here too.
   *
   * @param {BookmarkCategory} category - The category to check.
   * @return {void} Does not return a value.
   */
  private setConflictInfo(category: BookmarkCategory): void {
    const conflicting = this.store
      .findByBookmarkCategory(category._id)
      .filter(topic => topic._id !== this.itemToEdit?._id);
    if (conflicting.length > 0) {
      this.categoryConflicts[category._id] = conflicting
        .map(topic => topic.title)
        .join(', ');
    }
  }

  /**
   * Updates the form value by patching the 'bookmarkCategories' field with the IDs of the selected categories.
   *
   * The method retrieves the selected categories stored in the `selectedCategories` property, extracts their `_id` values,
   * and applies these values to the 'bookmarkCategories' field in the form state using the `patchValue` method.
   *
   * @*/
  updateFormValue(): void {
    this.form.patchValue({
      bookmarkCategories: this.selectedCategories.map(category => category._id),
    });
  }

  /**
   * Sets (or clears) the topic's color token.
   *
   * @param {ColorToken | null} token - The color token to select, or `null` to clear it.
   * @return {void} Does not return a value.
   */
  selectColor(token: ColorToken | null): void {
    this.form.get('color')?.setValue(token);
  }

  /** Whether the public and the internal atlas have their own landing page. */
  get separateLandingPages(): boolean {
    return hasSeparateLandingPages(
      this.globalStore.activeCommune()?.atlasConfig
    );
  }

  /**
   * Toggles whether the topic should be on the given landing page (the shared
   * one until they are separated). Only takes effect when the form is saved.
   *
   * @param {boolean} visible - Whether the topic should appear on the landing page.
   * @param {LandingPageAudience} [audience='internal'] - Public or internal landing page.
   * @return {void} Does not return a value.
   */
  onLandingPageToggle(
    visible: boolean,
    audience: LandingPageAudience = 'internal'
  ): void {
    if (audience === 'public') {
      this.showOnPublicLandingPage = visible;
    } else {
      this.showOnLandingPage = visible;
    }
    this.form.markAsDirty();
  }

  /**
   * Handles the tab change event by updating the active tab item and selected tab.
   *
   * @param {MenuItem} $event - The menu item representing the new active tab.
   * @return {void} This method does not return a value.
   */
  onTabChange($event: MenuItem): void {
    this.activeTabItem = $event;
    this.selectedTab = this.activeTabItem.id || 'general';
  }
}
